import { useRef, useState } from 'react'
import { processPacketDataForLogging } from 'mcraft-fun-mineflayer/build/packetsLogger'
import { PacketData } from '../../ReplayPanel'
import { useScrollBehavior } from '../../hooks/useScrollBehavior'
import { ClientOnMap } from '../../../generatedServerPackets'
import styles from './PacketList.module.css'

const formatters: Record<string, (data: any) => string> = {
  position: (data) => `x:${data.x.toFixed(2)} y:${data.y.toFixed(2)} z:${data.z.toFixed(2)}`,
  // chat: (data) => data,
  map_chunk (data: ClientOnMap['map_chunk'] | any) {
    const sizeOfChunk = data.chunkData?.length
    const blockEntitiesCount = data.blockEntities?.length
    return `x:${data.x} z:${data.z} C:${sizeOfChunk} E:${blockEntitiesCount}`
  },
  default: (data) => processPacketDataForLogging(data)
}

const getPacketIconType = (packet: PacketData): 'box' | 'target' => {
  if (packet.name.includes('block') || packet.name.includes('chunk') || packet.name.includes('light')) return 'box'
  if (packet.name.includes('entity') || packet.name.includes('player') || packet.name.includes('passenger')) return 'target'
  if (packet.name.includes('chat')) return 'target'
  return 'box'
}

interface Props {
  packets: readonly PacketData[]
  maxHeight?: number
}

function formatTimeDiff (current: number, prev: number | null): string {
  if (prev === null) return ''
  const diff = current - prev
  return `+${Math.floor(diff / 1000)}`
}

export default function PacketList ({ packets, maxHeight = 300 }: Props) {
  const listRef = useRef<HTMLDivElement>(null)
  const [expandedPacket, setExpandedPacket] = useState<number | null>(null)
  useScrollBehavior(listRef, { messages: packets, opened: true })

  let prevTimestamp: number | null = null

  return (
    <div
      ref={listRef}
      className={styles.list}
      style={{ ['--list-max' as any]: `calc(${maxHeight}px * var(--s))` }}
    >
      <div className={styles.inner}>
        {packets.map((packet) => {
          const timeDiff = formatTimeDiff(packet.timestamp, prevTimestamp)
          prevTimestamp = packet.timestamp
          const iconType = getPacketIconType(packet)
          const rowClassName = [
            styles.row,
            packet.isUpcoming ? styles.rowMuted : '',
            packet.actualVersion ? styles.rowServer : '',
          ]
            .filter(Boolean)
            .join(' ')

          return (
            <div key={`${packet.timestamp}-${packet.position}`}>
              <div
                className={rowClassName}
                onClick={() => setExpandedPacket(expandedPacket === packet.position ? null : packet.position)}
              >
                <span className={styles.icon} data-icon={iconType} />
                <span className={styles.meta}>#{packet.position}</span>
                <span className={styles.filteredIndex}>{timeDiff}</span>
                <span className={`${styles.name} ${packet.actualVersion ? styles.nameModified : ''}`}>
                  {packet.name}
                </span>
                <span className={styles.data}>
                  {formatters[packet.name]?.(packet.data) ?? formatters.default(packet.data)}
                </span>
              </div>
              {expandedPacket === packet.position && (
                <div className={styles.expanded}>
                  <div className={styles.expandedTitle}><strong>Data:</strong></div>
                  <pre className={styles.expandedPre}>
                    {JSON.stringify(JSON.parse(formatters.default(packet.data)), null, 2)}
                  </pre>
                  {packet.actualVersion && (
                    <>
                      <div className={styles.expandedTitle}><strong>Actual Version:</strong></div>
                      <pre className={styles.expandedPre}>
                        {JSON.stringify(JSON.parse(formatters.default(packet.actualVersion)), null, 2)}
                      </pre>
                    </>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
